import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit, Trash2, Eye, Upload, Save, X, LogOut } from 'lucide-react'
import { propertyAPI, authAPI } from '../services/api'

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('properties')
  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingProperty, setEditingProperty] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)
  const [properties, setProperties] = useState([])
  const [newProperty, setNewProperty] = useState({
    title: '',
    description: '',
    location: '',
    price: '',
    currency: 'TL',
    type: 'Satılık',
    openArea: '',
    closedArea: '',
    electricPower: '',
    images: []
  })
  
  const navigate = useNavigate()

  // Authentication kontrolü
  useEffect(() => {
    checkAuth()
  }, [])

  // Admin ilanlarını yükle
  useEffect(() => {
    if (user) {
      loadProperties()
    }
  }, [user])

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken')
    const userData = localStorage.getItem('adminUser')
    
    if (!token || !userData) {
      navigate('/admin-login')
      return
    }

    try {
      const response = await authAPI.verify(token)
      if (response.data.success) {
        setUser(JSON.parse(userData))
      } else {
        localStorage.removeItem('adminToken')
        localStorage.removeItem('adminUser')
        navigate('/admin-login')
      }
    } catch (error) {
      console.error('Auth verification failed:', error)
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminUser')
      navigate('/admin-login')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminUser')
    navigate('/admin-login')
  }

  // Eğer kullanıcı henüz yüklenmemişse loading göster
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yetkilendirme kontrol ediliyor...</p>
        </div>
      </div>
    )
  }

  const loadProperties = async () => {
    try {
      setLoading(true)
      const response = await propertyAPI.getAdminAll()
      setProperties(response.data)
    } catch (err) {
      setError('İlanlar yüklenirken hata oluştu')
      console.error('Veri yükleme hatası:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 10) // Maksimum 10 resim
    setNewProperty(prev => ({ ...prev, images: files }))
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewProperty(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      await propertyAPI.create(newProperty)
      
      // Form'u temizle
      setNewProperty({
        title: '',
        description: '',
        location: '',
        price: '',
        currency: 'TL',
        type: 'Satılık',
        openArea: '',
        closedArea: '',
        electricPower: '',
        images: []
      })
      setShowAddForm(false)
      
      // İlanları yeniden yükle
      await loadProperties()
      alert('İlan başarıyla eklendi!')
      
    } catch (err) {
      console.error('İlan ekleme hatası:', err)
      alert('İlan eklenirken hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  const deleteProperty = async (id) => {
    if (confirm('Bu ilanı silmek istediğinizden emin misiniz?')) {
      try {
        setLoading(true)
        await propertyAPI.delete(id)
        await loadProperties()
        alert('İlan silindi!')
      } catch (err) {
        console.error('İlan silme hatası:', err)
        alert('İlan silinirken hata oluştu!')
      } finally {
        setLoading(false)
      }
    }
  }

  const startEditProperty = (property) => {
    setEditingProperty({
      ...property,
      images: [] // Yeni resimler için
    })
    setShowEditForm(true)
    setShowAddForm(false)
  }

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditingProperty(prev => ({ ...prev, [name]: value }))
  }

  const handleEditFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 10)
    setEditingProperty(prev => ({ ...prev, images: files }))
  }

  const handleUpdateSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      await propertyAPI.update(editingProperty._id, editingProperty)
      
      setShowEditForm(false)
      setEditingProperty(null)
      
      // İlanları yeniden yükle
      await loadProperties()
      alert('İlan başarıyla güncellendi!')
      
    } catch (err) {
      console.error('İlan güncelleme hatası:', err)
      alert('İlan güncellenirken hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  const toggleFeatured = async (id) => {
    try {
      setLoading(true)
      await propertyAPI.toggleFeatured(id)
      await loadProperties()
    } catch (err) {
      console.error('Öne çıkarma hatası:', err)
      alert('İşlem sırasında hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              <span className="ml-4 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                Gizli Erişim
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Hoş geldiniz, {user?.username || 'Admin'}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Çıkış Yap"
              >
                <LogOut size={16} />
                <span>Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('properties')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'properties'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                İlan Yönetimi
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'messages'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Mesajlar
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'settings'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Ayarlar
              </button>
            </nav>
          </div>
        </div>

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  İlan Yönetimi
                </h2>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="btn-primary flex items-center"
                  disabled={loading}
                >
                  <Plus size={20} className="mr-2" />
                  Yeni İlan Ekle
                </button>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              {loading && (
                <div className="mb-6 p-4 bg-blue-100 text-blue-700 rounded-lg flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  İşlem gerçekleştiriliyor...
                </div>
              )}

              {/* Add Property Form */}
              {showAddForm && (
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Yeni İlan Ekle
                    </h3>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          İlan Başlığı *
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={newProperty.title}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Örn: Modern 3+1 Daire"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Konum *
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={newProperty.location}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Örn: Beşiktaş, İstanbul"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fiyat *
                        </label>
                        <div className="flex">
                          <input
                            type="number"
                            name="price"
                            value={newProperty.price}
                            onChange={handleInputChange}
                            required
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500"
                            placeholder="2500000"
                          />
                          <select
                            name="currency"
                            value={newProperty.currency}
                            onChange={handleInputChange}
                            className="px-4 py-3 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="TL">TL</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          İlan Türü *
                        </label>
                        <select
                          name="type"
                          value={newProperty.type}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="Satılık">Satılık</option>
                          <option value="Kiralık">Kiralık</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Açık Alan Metrekaresi (m²)
                        </label>
                        <input
                          type="number"
                          name="openArea"
                          value={newProperty.openArea}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kapalı Alan Metrekaresi (m²) *
                        </label>
                        <input
                          type="number"
                          name="closedArea"
                          value={newProperty.closedArea}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="120"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Elektrik Gücü (KW)
                        </label>
                        <input
                          type="number"
                          name="electricPower"
                          value={newProperty.electricPower}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="15"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Açıklama
                      </label>
                      <textarea
                        name="description"
                        value={newProperty.description}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                        placeholder="İlan açıklaması..."
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Resimler
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                          id="image-upload"
                        />
                        <label htmlFor="image-upload" className="cursor-pointer">
                          <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600">
                            Resim yüklemek için tıklayın
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            PNG, JPG, GIF (Maksimum 10 resim)
                          </p>
                        </label>
                        {newProperty.images.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-green-600">
                              {newProperty.images.length} resim seçildi
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        className="btn-primary flex items-center"
                      >
                        <Save size={20} className="mr-2" />
                        İlanı Kaydet
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="btn-secondary"
                      >
                        İptal
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Edit Property Form */}
              {showEditForm && editingProperty && (
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      İlan Düzenle: {editingProperty.title}
                    </h3>
                    <button
                      onClick={() => {
                        setShowEditForm(false)
                        setEditingProperty(null)
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <form onSubmit={handleUpdateSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          İlan Başlığı *
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={editingProperty.title}
                          onChange={handleEditInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Örn: Modern 3+1 Daire"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Konum *
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={editingProperty.location}
                          onChange={handleEditInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Örn: Beşiktaş, İstanbul"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fiyat *
                        </label>
                        <div className="flex">
                          <input
                            type="number"
                            name="price"
                            value={editingProperty.price}
                            onChange={handleEditInputChange}
                            required
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500"
                            placeholder="2500000"
                          />
                          <select
                            name="currency"
                            value={editingProperty.currency}
                            onChange={handleEditInputChange}
                            className="px-4 py-3 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="TL">TL</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          İlan Türü *
                        </label>
                        <select
                          name="type"
                          value={editingProperty.type}
                          onChange={handleEditInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="Satılık">Satılık</option>
                          <option value="Kiralık">Kiralık</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Açık Alan Metrekaresi (m²)
                        </label>
                        <input
                          type="number"
                          name="openArea"
                          value={editingProperty.openArea}
                          onChange={handleEditInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kapalı Alan Metrekaresi (m²) *
                        </label>
                        <input
                          type="number"
                          name="closedArea"
                          value={editingProperty.closedArea}
                          onChange={handleEditInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="120"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Elektrik Gücü (KW)
                        </label>
                        <input
                          type="number"
                          name="electricPower"
                          value={editingProperty.electricPower}
                          onChange={handleEditInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="15"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Açıklama
                      </label>
                      <textarea
                        name="description"
                        value={editingProperty.description}
                        onChange={handleEditInputChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                        placeholder="İlan açıklaması..."
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yeni Resimler Ekle
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleEditFileChange}
                          className="hidden"
                          id="edit-image-upload"
                        />
                        <label htmlFor="edit-image-upload" className="cursor-pointer">
                          <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600">
                            Yeni resim eklemek için tıklayın
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            PNG, JPG, GIF (Maksimum 10 resim)
                          </p>
                        </label>
                        {editingProperty.images.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-green-600">
                              {editingProperty.images.length} yeni resim seçildi
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        className="btn-primary flex items-center"
                        disabled={loading}
                      >
                        <Save size={20} className="mr-2" />
                        Değişiklikleri Kaydet
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditForm(false)
                          setEditingProperty(null)
                        }}
                        className="btn-secondary"
                      >
                        İptal
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Properties List */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İlan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Konum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fiyat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tarih
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {properties.map((property) => (
                      <tr key={property._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {property.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {property.type}
                              {property.featured && (
                                <span className="ml-2 px-2 py-1 bg-accent-100 text-accent-800 text-xs rounded-full">
                                  Öne Çıkan
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {property.location}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parseInt(property.price).toLocaleString()} {property.currency || 'TL'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            property.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {property.status === 'active' ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(property.createdAt || property.createdDate).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2 justify-end">
                            <button className="text-blue-600 hover:text-blue-900">
                              <Eye size={16} />
                            </button>
                            <button 
                              onClick={() => startEditProperty(property)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => toggleFeatured(property._id)}
                              className={`px-2 py-1 text-xs rounded ${
                                property.featured
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {property.featured ? 'Öne Çıkarılmış' : 'Öne Çıkar'}
                            </button>
                            <button
                              onClick={() => deleteProperty(property._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Gelen Mesajlar
            </h2>
            <p className="text-gray-600">
              Henüz mesaj bulunmuyor. İletişim formundan gelen mesajlar burada görünecek.
            </p>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Genel Ayarlar
            </h2>
            <p className="text-gray-600">
              Site ayarları ve yapılandırma seçenekleri burada bulunacak.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminPanel